import { useState } from 'react';
import {
  FileText,
  Table2,
  FileSignature,
  Plus,
  Trash2,
  GripVertical,
  Eye,
  EyeOff,
  Check,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { useTemplateDesigner } from '../TemplateDesignerContext';
import type { DocumentType, HeaderField } from '../types';

const DOCUMENT_TYPES: { id: DocumentType; label: string }[] = [
  { id: 'invoice', label: 'Invoice' },
  { id: 'quote', label: 'Quote' },
  { id: 'credit_memo', label: 'Credit Memo' },
  { id: 'purchase_order', label: 'Purchase Order' },
];

export function DocumentOptionsPanel() {
  const [activeTab, setActiveTab] = useState<'header' | 'table' | 'footer'>('header');

  return (
    <div className="space-y-4">
      {/* Tab Navigation */}
      <div className="flex gap-0.5 p-1 bg-muted rounded-lg">
        {[
          { id: 'header', icon: FileText, label: 'Header' },
          { id: 'table', icon: Table2, label: 'Table' },
          { id: 'footer', icon: FileSignature, label: 'Footer' },
        ].map(({ id, icon: Icon, label }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id as typeof activeTab)}
            className={`
              flex-1 flex items-center justify-center gap-1.5 py-2 px-2 rounded-md text-xs font-medium
              transition-all duration-200 min-w-0
              ${activeTab === id
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
              }
            `}
          >
            <Icon className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">{label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'header' && <HeaderOptions />}
        {activeTab === 'table' && <TableOptions />}
        {activeTab === 'footer' && <FooterOptions />}
      </div>
    </div>
  );
}

function HeaderOptions() {
  const { settings, updateSettings } = useTemplateDesigner();
  const { headerFields } = settings;
  const [isAddingField, setIsAddingField] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldDefaultValue, setNewFieldDefaultValue] = useState('');

  const toggleField = (fieldId: string) => {
    const updated = headerFields.map((f) =>
      f.id === fieldId ? { ...f, enabled: !f.enabled } : f
    );
    updateSettings('headerFields', updated);
  };

  const updateFieldDefaultValue = (fieldId: string, defaultValue: string) => {
    const updated = headerFields.map((f) =>
      f.id === fieldId ? { ...f, defaultValue } : f
    );
    updateSettings('headerFields', updated);
  };

  const addCustomField = () => {
    if (!newFieldName.trim()) return;
    const newField: HeaderField = {
      id: `custom_${Date.now()}`,
      name: newFieldName.trim(),
      enabled: true,
      isCustom: true,
      defaultValue: newFieldDefaultValue.trim() || undefined,
      documentTypes: ['invoice', 'quote', 'credit_memo', 'purchase_order'],
    };
    updateSettings('headerFields', [...headerFields, newField]);
    setNewFieldName('');
    setNewFieldDefaultValue('');
    setIsAddingField(false);
  };

  const removeField = (fieldId: string) => {
    updateSettings('headerFields', headerFields.filter((f) => f.id !== fieldId));
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Configure which fields appear in the document header
      </p>

      {/* Standard Fields */}
      <div className="space-y-2">
        {headerFields.map((field) => (
          <div
            key={field.id}
            className={`
              rounded-lg border transition-colors
              ${field.enabled ? 'bg-card border-border' : 'bg-muted/30 border-transparent'}
            `}
          >
            <div className="flex items-center justify-between p-3">
              <div className="flex items-center gap-3">
                <GripVertical className="w-4 h-4 text-muted-foreground/50 cursor-grab" />
                <button
                  onClick={() => toggleField(field.id)}
                  className="flex items-center gap-2"
                >
                  {field.enabled ? (
                    <Eye className="w-4 h-4 text-primary" />
                  ) : (
                    <EyeOff className="w-4 h-4 text-muted-foreground" />
                  )}
                  <span className={`text-sm font-medium ${!field.enabled && 'text-muted-foreground'}`}>
                    {field.name}
                  </span>
                </button>
                {field.isCustom && (
                  <span className="px-1.5 py-0.5 text-[10px] font-medium bg-primary/10 text-primary rounded">
                    Custom
                  </span>
                )}
              </div>
              {field.isCustom && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => removeField(field.id)}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
            {/* Default value input for custom fields */}
            {field.isCustom && field.enabled && (
              <div className="px-3 pb-3 pt-0">
                <Input
                  value={field.defaultValue || ''}
                  onChange={(e) => updateFieldDefaultValue(field.id, e.target.value)}
                  placeholder="Default value (optional)"
                  className="text-sm h-8"
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add Custom Field */}
      {isAddingField ? (
        <div className="space-y-3 p-3 rounded-lg border border-primary/30 bg-primary/5">
          <Input
            value={newFieldName}
            onChange={(e) => setNewFieldName(e.target.value)}
            placeholder="Field name (e.g., Job ID, Reference)"
            autoFocus
          />
          <Input
            value={newFieldDefaultValue}
            onChange={(e) => setNewFieldDefaultValue(e.target.value)}
            placeholder="Default value (optional)"
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={addCustomField} disabled={!newFieldName.trim()}>
              Add Field
            </Button>
            <Button size="sm" variant="ghost" onClick={() => {
              setIsAddingField(false);
              setNewFieldName('');
              setNewFieldDefaultValue('');
            }}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="outline"
          className="w-full"
          onClick={() => setIsAddingField(true)}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Custom Field
        </Button>
      )}
    </div>
  );
}

function TableOptions() {
  const { settings, updateSettings } = useTemplateDesigner();
  const { tableColumns, dateFormat } = settings;

  const toggleColumn = (columnId: string) => {
    const updated = tableColumns.map((c) =>
      c.id === columnId ? { ...c, enabled: !c.enabled } : c
    );
    updateSettings('tableColumns', updated);
  };

  // date-fns format tokens (API expects these exact formats)
  const DATE_FORMATS = [
    { value: 'MM/dd/yyyy', label: '12/24/2025' },
    { value: 'dd/MM/yyyy', label: '24/12/2025' },
    { value: 'yyyy-MM-dd', label: '2025-12-24' },
    { value: 'MMM d, yyyy', label: 'Dec 24, 2025' },
  ];

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Configure table columns and formatting
      </p>

      {/* Column Toggles */}
      <div className="space-y-3">
        <Label className="text-sm font-semibold">Visible Columns</Label>
        <div className="grid grid-cols-2 gap-2">
          {tableColumns.map((column) => (
            <button
              key={column.id}
              onClick={() => toggleColumn(column.id)}
              className={`
                flex items-center gap-2 p-3 rounded-lg border-2 transition-all
                ${column.enabled
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/30'
                }
              `}
            >
              <div className={`
                w-4 h-4 rounded border-2 flex items-center justify-center transition-colors
                ${column.enabled ? 'bg-primary border-primary' : 'border-muted-foreground/30'}
              `}>
                {column.enabled && (
                  <Check className="w-3 h-3 text-primary-foreground" />
                )}
              </div>
              <span className={`text-sm font-medium ${!column.enabled && 'text-muted-foreground'}`}>
                {column.name}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Date Format */}
      <div className="space-y-3">
        <Label className="text-sm font-semibold">Date Format</Label>
        <div className="grid grid-cols-2 gap-2">
          {DATE_FORMATS.map((format) => (
            <button
              key={format.value}
              onClick={() => updateSettings('dateFormat', format.value)}
              className={`
                p-2 rounded-lg border-2 text-sm transition-all
                ${dateFormat === format.value
                  ? 'border-primary bg-primary/5 font-medium'
                  : 'border-border hover:border-primary/30'
                }
              `}
            >
              {format.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function FooterOptions() {
  const { settings, updateNestedSettings } = useTemplateDesigner();
  const { footer } = settings;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Configure footer content and signatures
      </p>

      <Accordion type="single" collapsible className="w-full">
        {/* Generic Note */}
        <AccordionItem value="generic-note">
          <AccordionTrigger className="text-sm font-semibold">
            <div className="flex items-center gap-2">
              <Switch
                checked={footer.genericNote.enabled}
                onCheckedChange={(checked) =>
                  updateNestedSettings('footer', {
                    genericNote: { ...footer.genericNote, enabled: checked },
                  })
                }
                onClick={(e) => e.stopPropagation()}
              />
              Generic Note
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <Textarea
              value={footer.genericNote.content}
              onChange={(e) =>
                updateNestedSettings('footer', {
                  genericNote: { ...footer.genericNote, content: e.target.value },
                })
              }
              placeholder="Thank you for your business!"
              className="min-h-[80px]"
            />
          </AccordionContent>
        </AccordionItem>

        {/* Document-Specific Notes */}
        <AccordionItem value="doc-notes">
          <AccordionTrigger className="text-sm font-semibold">
            Document-Specific Notes
          </AccordionTrigger>
          <AccordionContent className="space-y-4">
            {DOCUMENT_TYPES.map((docType) => (
              <div key={docType.id} className="space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={footer.documentNotes[docType.id].enabled}
                    onCheckedChange={(checked) =>
                      updateNestedSettings('footer', {
                        documentNotes: {
                          ...footer.documentNotes,
                          [docType.id]: {
                            ...footer.documentNotes[docType.id],
                            enabled: checked as boolean,
                          },
                        },
                      })
                    }
                  />
                  <Label className="text-sm">{docType.label}</Label>
                </div>
                {footer.documentNotes[docType.id].enabled && (
                  <Textarea
                    value={footer.documentNotes[docType.id].content}
                    onChange={(e) =>
                      updateNestedSettings('footer', {
                        documentNotes: {
                          ...footer.documentNotes,
                          [docType.id]: {
                            ...footer.documentNotes[docType.id],
                            content: e.target.value,
                          },
                        },
                      })
                    }
                    placeholder={`Note for ${docType.label.toLowerCase()}s...`}
                    className="min-h-[60px]"
                  />
                )}
              </div>
            ))}
          </AccordionContent>
        </AccordionItem>

        {/* Terms & Conditions */}
        <AccordionItem value="terms">
          <AccordionTrigger className="text-sm font-semibold">
            <div className="flex items-center gap-2">
              <Switch
                checked={footer.termsAndConditions.enabled}
                onCheckedChange={(checked) =>
                  updateNestedSettings('footer', {
                    termsAndConditions: { ...footer.termsAndConditions, enabled: checked },
                  })
                }
                onClick={(e) => e.stopPropagation()}
              />
              Terms & Conditions
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <Textarea
              value={footer.termsAndConditions.content}
              onChange={(e) =>
                updateNestedSettings('footer', {
                  termsAndConditions: { ...footer.termsAndConditions, content: e.target.value },
                })
              }
              placeholder="Enter your terms and conditions..."
              className="min-h-[120px]"
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Additional Options */}
      <div className="space-y-3 pt-2">
        <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
          <Label className="text-sm font-medium">Show Tax Breakdown</Label>
          <Switch
            checked={footer.taxBreakdown}
            onCheckedChange={(checked) => updateNestedSettings('footer', { taxBreakdown: checked })}
          />
        </div>
        <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
          <Label className="text-sm font-medium">Show Payment Instructions</Label>
          <Switch
            checked={footer.showPaymentInstructions ?? false}
            onCheckedChange={(checked) => updateNestedSettings('footer', { showPaymentInstructions: checked })}
          />
        </div>
      </div>
    </div>
  );
}
